import { forwardRef, useState } from "react";

/**
 * TextInput — the base text-entry primitive. Number and search inputs
 * reuse this component with different `type`/icon props rather than
 * duplicating markup (see NumberInput.jsx, SearchInput.jsx).
 *
 * @param {string} label
 * @param {string} error — validation error message, renders below the field
 * @param {string} hint — helper text, hidden when `error` is present
 * @param {React.ReactNode} leftIcon
 * @param {React.ReactNode} rightSlot — e.g. a "clear" button or unit label
 */
const TextInput = forwardRef(function TextInput(
  {
    label,
    error,
    hint,
    leftIcon = null,
    rightSlot = null,
    className = "",
    id,
    ...rest
  },
  ref
) {
  const [isFocused, setIsFocused] = useState(false);
  const inputId = id || rest.name;

  return (
    <div className="w-full">
      {label && (
        <label
          htmlFor={inputId}
          className="block text-caption uppercase tracking-wide text-brand-muted mb-1.5"
        >
          {label}
        </label>
      )}
      <div
        className={[
          "flex items-center gap-2 rounded-lg bg-white border px-3.5 h-12 transition-colors duration-150",
          error
            ? "border-brand-danger"
            : isFocused
              ? "border-brand-primary"
              : "border-brand-line",
        ].join(" ")}
      >
        {leftIcon && <span className="text-brand-muted flex-shrink-0">{leftIcon}</span>}
        <input
          ref={ref}
          id={inputId}
          onFocus={(e) => {
            setIsFocused(true);
            rest.onFocus?.(e);
          }}
          onBlur={(e) => {
            setIsFocused(false);
            rest.onBlur?.(e);
          }}
          className={[
            "flex-1 min-w-0 bg-transparent outline-none text-body-md text-brand-ink placeholder:text-brand-muted",
            className,
          ].join(" ")}
          {...rest}
        />
        {rightSlot && <span className="flex-shrink-0">{rightSlot}</span>}
      </div>
      {error ? (
        <p className="mt-1.5 text-body-sm text-brand-danger">{error}</p>
      ) : hint ? (
        <p className="mt-1.5 text-body-sm text-brand-muted">{hint}</p>
      ) : null}
    </div>
  );
});

export default TextInput;
